import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { db } from '../services/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { ArrowLeft, ExternalLink, Github } from 'lucide-react';

interface Project {
    id: string;
    title: string;
    description: string;
    content?: string;
    technologies: string[];
    github?: string;
    demo?: string;
    image?: string;
    category: 'core' | 'side';
    order: number;
}

const ProjectDetailPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [project, setProject] = useState<Project | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchProject = async () => {
            if (!id) return;
            try {
                const docRef = doc(db, 'projects', id);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    setProject({ id: docSnap.id, ...docSnap.data() } as Project);
                } else {
                    setError('Project not found.');
                }
            } catch (err) {
                setError('Failed to load project.');
            } finally {
                setLoading(false);
            }
        };
        fetchProject();
    }, [id]);

    if (loading) {
        return (
            <div className="mx-auto max-w-3xl md:border-x px-4 py-24">
                <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-foreground" />
                </div>
            </div>
        );
    }

    if (error || !project) {
        return (
            <div className="mx-auto max-w-3xl md:border-x px-4 py-24">
                <p className="text-sm text-muted-foreground text-center">{error || 'Project not found.'}</p>
                <div className="text-center mt-4">
                    <button
                        onClick={() => navigate('/projects')}
                        className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                    >
                        ← Back to Projects
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="mx-auto max-w-3xl md:border-x">
            <div className="px-4 py-12">
                {/* Back button */}
                <motion.button
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    onClick={() => navigate('/projects')}
                    className="mb-8 text-sm text-muted-foreground hover:text-foreground flex items-center gap-2 transition-colors"
                >
                    <ArrowLeft className="w-4 h-4" />
                    Back to Projects
                </motion.button>

                <motion.article
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    {/* Header */}
                    <header className="mb-8">
                        <div className="flex items-center gap-2 mb-3">
                            <span className="px-2 py-0.5 text-xs font-mono text-muted-foreground border border-border rounded">
                                {project.category === 'core' ? 'Built to Last' : 'Side Project'}
                            </span>
                        </div>
                        <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4 leading-tight">
                            {project.title}
                        </h1>
                        <p className="text-base text-muted-foreground leading-relaxed">
                            {project.description}
                        </p>
                    </header>

                    {/* Links */}
                    <div className="flex items-center gap-3 py-4 screen-line-before screen-line-after mb-8">
                        {project.github && (
                            <a
                                href={project.github}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 px-4 py-2 text-sm border border-border rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                            >
                                <Github size={14} />
                                Source Code
                            </a>
                        )}
                        {project.demo && (
                            <a
                                href={project.demo}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 px-4 py-2 text-sm border border-border rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                            >
                                <ExternalLink size={14} />
                                Live Demo
                            </a>
                        )}
                    </div>

                    {/* Project Image */}
                    {project.image && (
                        <img
                            src={project.image}
                            alt={project.title}
                            className="w-full h-auto max-h-96 object-cover rounded-lg mb-8 border border-border"
                        />
                    )}

                    {/* Technologies */}
                    <div className="mb-8">
                        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                            Tech Stack
                        </h2>
                        <div className="flex flex-wrap gap-2">
                            {project.technologies.map((tech) => (
                                <span
                                    key={tech}
                                    className="px-2 py-0.5 text-xs font-mono text-muted-foreground border border-border rounded"
                                >
                                    {tech}
                                </span>
                            ))}
                        </div>
                    </div>

                    {/* Content (rich text) */}
                    {project.content && (
                        <div className="prose prose-lg max-w-none">
                            <div dangerouslySetInnerHTML={{ __html: project.content }} />
                        </div>
                    )}
                </motion.article>
            </div>
        </div>
    );
};

export default ProjectDetailPage;
